import React, { useRef, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Divider } from "@mui/material";
import { PDFDownloadLink, Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer';
import dayjs from "dayjs";
import generatePDF from "react-to-pdf";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import { convertMinutesToCustomFormat } from '../common/CommonSnippet';

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: 'Helvetica',
  },
  section: {
    marginBottom: 10,
    fontSize: 10,
  },
  table: {
    display: "table",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tableRow: {
    flexDirection: "row",
  },
  tableCol: {
    width: "50%",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  tableCellHead: {
    margin: 5,
    fontSize: 12,
    fontWeight: "Bold",
  },
  tableCell: {
    margin: 5,
    fontSize: 10,
  },
});

const DashboardPDF = ({ open, onClose, overallReport = {} }) => {
  const reportData = overallReport?.OverallReport || {};
  const formattedStartDate = reportData?.StartDate ? dayjs(reportData.StartDate).format("DD MMM YYYY") : "N/A";
  const formattedEndDate = reportData?.EndDate ? dayjs(reportData.EndDate).format("DD MMM YYYY") : "N/A";
  const [fileName, setFileName] = useState("IRA_Report.pdf");
  const pdfRef = useRef();

  const handleDownload = async () => {
    if (pdfRef.current) {

      try {
        await generatePDF(pdfRef, { filename: fileName });
      } catch (error) {
        console.error('Error generating PDF:', error);
      } 
    }
  };
  const handleDownloadXLSX = () => {
    const wb = XLSX.utils.book_new();

    const addSheet = (data, sheetName) => {
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    };

    if (reportData.FunctionDetails?.length > 0) {
      const functionData = [
        ["Function", "Count"],
        ...reportData.FunctionDetails.map((row) => [row.FunctionName, row.Count]),
      ];
      addSheet(functionData, "Function Usage");
    }

    if (reportData.BrowserDetails?.length > 0) {
      const browserData = [
        ["Browser", "Count"],
        ...reportData.BrowserDetails.map((row) => [row.BrowserName, row.Count]),
      ];
      addSheet(browserData, "Browser Usage");
    }

    if (reportData.LocationDetails?.length > 0) {
      const locationData = [
        ["Location", "Count"],
        ...reportData.LocationDetails
          .filter((row) => row.CountryCode !== "NA")
          .map((row) => [row.CountryCode, row.Count]),
      ];
      addSheet(locationData, "Location Details");
    }

    if (reportData.UserDetails?.length > 0) {
      const userData = [
        ["Corp Name", "Name","User Type", "Frequent Function", "Hours Spent", "First Session", "Last Session"],
        ...reportData.UserDetails.map((row) => [
          row.CorpName,
          `${row.FirstName} ${row.LastName}`,
          `${row?.IsTraneEmployee === 1 ? "Trane Technologies" : "Customer"}`,
          row.FrequentFunction,
          convertMinutesToCustomFormat(row.TotalMinutesSpent),
          row.FirstSession,
          row.LastSession,
        ]),
      ];
      addSheet(userData, "Tool Usage Details");
    }

    XLSX.writeFile(wb, "IRA_Analytics_Report.xlsx");
  };

  const handleDownloadCSV = () => {
    const csvData = [];

    const addSection = (data, sectionName) => {
      csvData.push([sectionName]);
      csvData.push(...data);
      csvData.push([]);
    };

    if (reportData.FunctionDetails?.length > 0) {
      const functionData = [
        ["Function", "Count"],
        ...reportData.FunctionDetails.map((row) => [row.FunctionName, row.Count]),
      ];
      addSection(functionData, "Function Usage");
    }

    if (reportData.BrowserDetails?.length > 0) {
      const browserData = [
        ["Browser", "Count"],
        ...reportData.BrowserDetails.map((row) => [row.BrowserName, row.Count]),
      ];
      addSection(browserData, "Browser Usage");
    }

    if (reportData.LocationDetails?.length > 0) {
      const locationData = [
        ["Location", "Count"],
        ...reportData.LocationDetails.filter((row) => row.CountryCode !== "NA").map((row) => [row.CountryCode, row.Count]),
      ];
      addSection(locationData, "Location Details");
    }

    if (reportData.UserDetails?.length > 0) {
      const userData = [
        ["Hours Spent", "Name","User Type", "Frequent Function"],
        ...reportData.UserDetails.map((row) => [
          row.HoursSpent,
          `${row.FirstName} ${row.LastName}`,
          `${row?.IsTraneEmployee === 1 ? "Trane Technologies" : "Customer"}`,
          row.FrequentFunction,
        ]),
      ];
      addSection(userData, "Tool Usage Details");
    }

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "IRA_Analytics_Report.csv");
  };
  const MyDocument = () => (
    <Document>
      <Page style={styles.page}>
        <View style={styles.section}>
          <Text style={{ fontSize: 12, fontWeight: 'bold', textAlign: 'center' }}>IRA Analytics Report</Text>
          <Text style={{ textAlign: 'center' }}>Tool Activity Analytics</Text>
          {(formattedStartDate !== 'N/A' && formattedEndDate !== 'N/A') &&
            <Text style={{ textAlign: 'center' }}>{formattedStartDate} to {formattedEndDate}</Text>
          }
        </View>
        <View style={styles.section}>
          <Text style={{ fontSize: 10, fontWeight: 'bold' }}>User Summary</Text>
          <Text>Total Users: {reportData?.TotalUser || 0}</Text>
          <Text>Total Hours Spent: {convertMinutesToCustomFormat(reportData?.TotalMinutes) || 0} hours</Text>
          <Text>Max Used Function: {reportData?.MaxUsedFunction[0]?.FunctionName || "All"}</Text>
        </View>

        {reportData?.FunctionDetails?.length > 0 && (
          <View style={styles.section}>
            <Text style={{ fontSize: 12, fontWeight: 'bold' }}> Function Usage</Text>
            <View style={styles.table}>
              <View style={styles.tableRow}>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHead}>Function</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHead}>Count</Text>
                </View>
              </View>
              {reportData.FunctionDetails.map((row, index) => (
                <View style={styles.tableRow} key={index}>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCell}>{row.FunctionName}</Text>
                  </View>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCell}>{row.Count}</Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        )}
        {reportData?.BrowserDetails?.length > 0 && (
          <View style={styles.section}>
            <Text style={{ fontSize: 12, fontWeight: 'bold' }}> Browser Usage</Text>
            <View style={styles.table}>
              <View style={styles.tableRow}>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHead}>Browser</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHead}>Count</Text>
                </View>
              </View>
              {reportData.BrowserDetails.map((row, index) => (
                <View style={styles.tableRow} key={index}>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCell}>{row.BrowserName}</Text>
                  </View>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCell}>{row.Count}</Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        )}
        {reportData?.LocationDetails?.length > 0 && (
          <View style={styles.section}>
            <Text style={{ fontSize: 12, fontWeight: 'bold' }}> Location Details</Text>
            <View style={styles.table}>
              <View style={styles.tableRow}>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHead}>Location</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHead}>Count</Text>
                </View>
              </View>
              {reportData.LocationDetails.filter((row) => row.CountryCode !== "NA").map((row, index) => (
                <View style={styles.tableRow} key={index}>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCell}>{row.CountryCode}</Text>
                  </View>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCell}>{row.Count}</Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        )}
        {reportData?.UserDetails?.length > 0 && (
          <View style={styles.section}>
            <Text style={{ fontSize: 12, fontWeight: 'bold' }}> Tool Usage Details</Text>
            <View style={styles.table}>
              <View style={styles.tableRow}>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHead}>Corp Name</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHead}>Name</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHead}>User Type</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHead}>Frequent Function</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHead}>Hours Spent</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHead}>First Session</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHead}>Last Session</Text>
                </View>
              </View>
              {reportData.UserDetails.map((row, index) => (
                <View style={styles.tableRow} key={index}>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCell}>{row.CorpName}</Text>
                  </View>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCell}>{`${row.FirstName} ${row.LastName}`}</Text>
                  </View>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCell}>{`${row?.IsTraneEmployee === 1 ? "Trane Technologies" : "Customer"}`}</Text>
                  </View>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCell}>{row.FrequentFunction}</Text>
                  </View>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCell}>{convertMinutesToCustomFormat(row.TotalMinutesSpent)}</Text>
                  </View>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCell}>{row.FirstSession}</Text>
                  </View>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCell}>{row.LastSession}</Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        )}
      </Page>
    </Document>
  );
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold', textAlign: 'center', paddingBottom: 0 }}>IRA Analytics Report</DialogTitle>
      <DialogContent>
        <Box ref={pdfRef} sx={{ padding: "11px", background: "#fff", fontFamily: "Arial, sans-serif" }}>

          {/* Header Section */}
          <Box sx={{ textAlign: 'center', marginBottom: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>Tool Activity Analytics</Typography>
            {(formattedStartDate !== 'N/A' && formattedEndDate !== 'N/A') &&
              <Typography variant="body2" color="textSecondary">
                {formattedStartDate} to {formattedEndDate}
              </Typography>
            }

            {/* <Typography variant="body2" color="textSecondary">User Type: {reportData?.UserType || "All"}</Typography> */}
          </Box>

          <Divider sx={{ marginY: 2 }} />

          {/* User Details */}
          <Typography variant="h6">📌 User Summary</Typography>
          <Typography>Total Users: {reportData?.TotalUser || 0}</Typography>
          <Typography>Total Hours Spent: {convertMinutesToCustomFormat(reportData?.TotalMinutes)  || 0} hours</Typography>
          <Typography>Max Used Function: {reportData?.MaxUsedFunction[0]?.FunctionName || "ALL"} </Typography>

          <Divider sx={{ marginY: 2 }} />

          {reportData?.FunctionDetails?.length > 0 && (
            <>
              <Typography variant="h6">🔧 Function Usage</Typography>
              <TableContainer component={Paper} sx={{ marginBottom: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Function</TableCell>
                      <TableCell align="right">Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportData.FunctionDetails.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.FunctionName}</TableCell>
                        <TableCell align="right">{row.Count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
          {reportData?.BrowserDetails?.length > 0 && (
            <>
              <Typography variant="h6">🌐 Browser Usage</Typography>
              <TableContainer component={Paper} sx={{ marginBottom: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Browser</TableCell>
                      <TableCell align="right">Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportData.BrowserDetails.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.BrowserName}</TableCell>
                        <TableCell align="right">{row.Count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
          {/* Location Details Table */}
          {reportData?.LocationDetails?.length > 0 && (
            <>
              <Typography variant="h6">📍 Location Details</Typography>
              <TableContainer component={Paper} sx={{ marginBottom: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Location</TableCell>
                      <TableCell align="right">Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportData.LocationDetails.filter((row) => row.CountryCode !== "NA").map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.CountryCode}</TableCell>
                        <TableCell align="right">{row.Count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {reportData?.UserDetails?.length > 0 && (
            <>
              <Typography variant="h6">👤 Tool Usage Details</Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>CorpName</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>User Type</TableCell>
                      <TableCell>Frequent Function</TableCell>
                      <TableCell>Hours Spent</TableCell>
                      <TableCell>First Session</TableCell>
                      <TableCell align="right">Last Session</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportData.UserDetails.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.CorpName}</TableCell>
                        <TableCell>{`${row.FirstName} ${row.LastName}`}</TableCell>
                        <TableCell>{`${row?.IsTraneEmployee === 1 ? "Trane Technologies" : "Customer"}`}</TableCell>
                        <TableCell >{row.FrequentFunction}</TableCell>
                        <TableCell>{convertMinutesToCustomFormat(row.TotalMinutesSpent)}</TableCell>
                        <TableCell >{row.FirstSession}</TableCell>
                        <TableCell align="right">{row.LastSession}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <PDFDownloadLink className="pdflink" style={{ background: "#7030a0", color: '#fff', borderRadius: '5px', padding: '6px 16px', lineHeight: '1.75', textDecoration: 'none', fontFamily: '"Roboto","Helvetica","Arial",sans-serif', fontWeight: "500" }} document={<MyDocument />} fileName="IRA_Analytics_Report.pdf">
          {({ loading }) => (loading ? 'Loading document...' : 'DOWNLOAD REPORT')}
        </PDFDownloadLink>
        <Button style={{ background: "#7030a0", color: '#fff' }} onClick={handleDownloadXLSX} variant="contained">Download XLSX</Button>
        <Button onClick={onClose} variant="outlined">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DashboardPDF;
